import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Space,
  Popconfirm,
  message,
  Card,
  Typography,
  Tooltip,
  Tag,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Input,
  Descriptions,
  Divider,
  Progress,
  Alert,
  Spin,
  Empty,
  Segmented,
} from 'antd';
import {
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ClearOutlined,
  FilterOutlined,
  BugOutlined,
  BarChartOutlined,
  LineChartOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Line } from '@ant-design/charts';
import PagesWrapper from '../../../PagesWrapper';
import logsService, { LogLevels, LogLevelColors, TimelinePeriods } from '../../../services/logsService';
import styles from './ExceptionLogs.module.css';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

/**
 * Exception Logs Management Page
 * Admin page for viewing, analyzing, and managing system logs
 */
const ExceptionLogs = () => {
  const { t } = useTranslation();
  
  // Data state
  const [logs, setLogs] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Timeline state
  const [timelineData, setTimelineData] = useState([]);
  const [timelinePeriod, setTimelinePeriod] = useState(TimelinePeriods.Week);
  const [timelineLoading, setTimelineLoading] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  
  // Filters
  const [filters, setFilters] = useState({
    level: null,
    dateRange: null,
    correlationId: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Modals
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [bulkDeleteModalVisible, setBulkDeleteModalVisible] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  /**
   * Fetch logs from API
   */
  const fetchLogs = useCallback(async (page = 1, pageSize = 50) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
      };
      
      if (filters.level) params.level = filters.level;
      if (filters.dateRange && filters.dateRange[0]) {
        params.from = filters.dateRange[0].toISOString();
      }
      if (filters.dateRange && filters.dateRange[1]) {
        params.to = filters.dateRange[1].toISOString();
      }
      if (filters.correlationId) {
        params.correlationId = filters.correlationId;
      }

      const response = await logsService.getLogs(params);
      setLogs(response.items || []);
      setPagination({
        current: response.page,
        pageSize: response.pageSize,
        total: response.totalCount,
      });
    } catch (error) {
      message.error(t('admin.logs.fetchError', 'Failed to load logs'));
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  /**
   * Fetch statistics
   */
  const fetchStatistics = useCallback(async () => {
    setStatsLoading(true);
    try {
      const stats = await logsService.getStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  /**
   * Fetch timeline data
   */
  const fetchTimeline = useCallback(async (period = timelinePeriod) => {
    setTimelineLoading(true);
    try {
      const response = await logsService.getTimeline(period);
      // Transform buckets into chart data format
      const chartData = [];
      response.buckets?.forEach(bucket => {
        const timestamp = new Date(bucket.timestamp);
        const label = period === TimelinePeriods.Day 
          ? timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : timestamp.toLocaleDateString([], { month: 'short', day: 'numeric' });
        
        chartData.push({ time: label, value: bucket.errorCount, type: 'Error' });
        chartData.push({ time: label, value: bucket.warningCount, type: 'Warning' });
        chartData.push({ time: label, value: bucket.criticalCount, type: 'Critical' });
        chartData.push({ time: label, value: bucket.informationCount, type: 'Information' });
      });
      setTimelineData(chartData);
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
    } finally {
      setTimelineLoading(false);
    }
  }, [timelinePeriod]);

  /**
   * Extract IP address from metadata
   */
  const getIpFromMeta = (meta) => {
    if (!meta) return null;
    try {
      const parsed = typeof meta === 'string' ? JSON.parse(meta) : meta;
      return parsed?.RemoteIpAddress || parsed?.remoteIpAddress || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    fetchLogs(pagination.current, pagination.pageSize);
    fetchStatistics();
    fetchTimeline();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handle timeline period change
   */
  const handleTimelinePeriodChange = (period) => {
    setTimelinePeriod(period);
    fetchTimeline(period);
  };

  /**
   * Handle table pagination changes
   */
  const handleTableChange = (newPagination, tableFilters, sorter) => {
    fetchLogs(newPagination.current, newPagination.pageSize);
  };

  /**
   * Apply filters
   */
  const applyFilters = () => {
    fetchLogs(1, pagination.pageSize);
  };

  /**
   * Reset filters
   */
  const resetFilters = () => {
    setFilters({
      level: null,
      dateRange: null,
      correlationId: '',
    });
    setTimeout(() => fetchLogs(1, pagination.pageSize), 0);
  };

  /**
   * View log detail
   */
  const viewLogDetail = async (log) => {
    try {
      const fullLog = await logsService.getLog(log.id);
      setSelectedLog(fullLog);
      setDetailModalVisible(true);
    } catch (error) {
      message.error(t('admin.logs.detailError', 'Failed to load log details'));
    }
  };

  /**
   * Delete single log
   */
  const handleDelete = async (id) => {
    try {
      await logsService.deleteLog(id);
      message.success(t('admin.logs.deleteSuccess', 'Log entry deleted'));
      fetchLogs(pagination.current, pagination.pageSize);
      fetchStatistics();
    } catch (error) {
      message.error(t('admin.logs.deleteError', 'Failed to delete log entry'));
    }
  };

  /**
   * Bulk delete logs
   */
  const handleBulkDelete = async (criteria) => {
    setBulkDeleteLoading(true);
    try {
      const result = await logsService.deleteLogs(criteria);
      message.success(result.message || `Deleted ${result.deletedCount} log entries`);
      setBulkDeleteModalVisible(false);
      fetchLogs(1, pagination.pageSize);
      fetchStatistics();
    } catch (error) {
      message.error(t('admin.logs.bulkDeleteError', 'Failed to delete logs'));
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  /**
   * Delete all logs
   */
  const handleDeleteAll = async () => {
    setBulkDeleteLoading(true);
    try {
      const result = await logsService.deleteAllLogs();
      message.success(result.message || 'All logs deleted');
      setBulkDeleteModalVisible(false);
      fetchLogs(1, pagination.pageSize);
      fetchStatistics();
    } catch (error) {
      message.error(t('admin.logs.deleteAllError', 'Failed to delete all logs'));
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  /**
   * Get level icon
   */
  const getLevelIcon = (level) => {
    switch (level) {
      case 'Error':
        return <CloseCircleOutlined style={{ color: LogLevelColors.Error }} />;
      case 'Warning':
        return <WarningOutlined style={{ color: LogLevelColors.Warning }} />;
      case 'Critical':
        return <ExclamationCircleOutlined style={{ color: LogLevelColors.Critical }} />;
      case 'Information':
      default:
        return <InfoCircleOutlined style={{ color: LogLevelColors.Information }} />;
    }
  };

  /**
   * Get level badge class
   */
  const getLevelClass = (level) => {
    switch (level) {
      case 'Error': return styles.levelError;
      case 'Warning': return styles.levelWarning;
      case 'Critical': return styles.levelCritical;
      default: return styles.levelInfo;
    }
  };

  /**
   * Parse and format metadata
   */
  const formatMetadata = (meta) => {
    if (!meta) return null;
    try {
      const parsed = typeof meta === 'string' ? JSON.parse(meta) : meta;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return meta;
    }
  };

  /**
   * Calculate error percentage
   */
  const getErrorPercentage = () => {
    if (!statistics || statistics.totalCount === 0) return 0;
    return Math.round(((statistics.errorCount + statistics.criticalCount) / statistics.totalCount) * 100);
  };

  /**
   * Table columns configuration
   */
  const columns = [
    {
      title: t('admin.logs.columns.level', 'Level'),
      dataIndex: 'level',
      key: 'level',
      width: 120,
      render: (level) => (
        <span className={`${styles.levelBadge} ${getLevelClass(level)}`}>
          {getLevelIcon(level)}
          <span style={{ marginLeft: 4 }}>{level}</span>
        </span>
      ),
      filters: Object.keys(LogLevels).map(key => ({ text: key, value: key })),
      onFilter: (value, record) => record.level === value,
    },
    {
      title: t('admin.logs.columns.timestamp', 'Timestamp'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp) => (
        <Tooltip title={new Date(timestamp).toLocaleString()}>
          <span>{logsService.getRelativeTime(timestamp)}</span>
        </Tooltip>
      ),
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
      defaultSortOrder: 'descend',
    },
    {
      title: t('admin.logs.columns.message', 'Message'),
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (message, record) => (
        <Tooltip title={message}>
          <span className={styles.messageCell}>
            {record.exception && <BugOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />}
            {message}
          </span>
        </Tooltip>
      ),
    },
    {
      title: t('admin.logs.columns.correlationId', 'Correlation ID'),
      dataIndex: 'correlationId',
      key: 'correlationId',
      width: 150,
      ellipsis: true,
      render: (id) => id ? (
        <Tooltip title={id}>
          <Tag style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {id.slice(0, 8)}...
          </Tag>
        </Tooltip>
      ) : '-',
    },
    {
      title: t('admin.logs.columns.ipAddress', 'IP Address'),
      key: 'ipAddress',
      width: 130,
      render: (_, record) => {
        const ip = getIpFromMeta(record.meta);
        return ip ? (
          <Tooltip title={t('admin.logs.ipTooltip', 'Client IP Address')}>
            <Tag icon={<GlobalOutlined />} style={{ fontFamily: 'monospace' }}>
              {ip}
            </Tag>
          </Tooltip>
        ) : '-';
      },
    },
    {
      title: t('admin.logs.columns.actions', 'Actions'),
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('common.view', 'View')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => viewLogDetail(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('admin.logs.deleteConfirm', 'Delete this log entry?')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes', 'Yes')}
            cancelText={t('common.no', 'No')}
          >
            <Tooltip title={t('common.delete', 'Delete')}>
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /**
   * Render statistics cards
   */
  const renderStatistics = () => (
    <Row gutter={[16, 16]} className={styles.statsRow}>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.logs.stats.total', 'Total Logs')}
            value={statistics?.totalCount || 0}
            valueStyle={{ color: '#52c41a' }}
            prefix={<BarChartOutlined />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.logs.stats.errors', 'Errors')}
            value={statistics?.errorCount || 0}
            valueStyle={{ color: '#ff4d4f' }}
            prefix={<CloseCircleOutlined />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.logs.stats.warnings', 'Warnings')}
            value={statistics?.warningCount || 0}
            valueStyle={{ color: '#faad14' }}
            prefix={<WarningOutlined />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.logs.stats.critical', 'Critical')}
            value={statistics?.criticalCount || 0}
            valueStyle={{ color: '#722ed1' }}
            prefix={<ExclamationCircleOutlined />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Statistic
            title={t('admin.logs.stats.info', 'Information')}
            value={statistics?.informationCount || 0}
            valueStyle={{ color: '#1890ff' }}
            prefix={<InfoCircleOutlined />}
          />
        </Card>
      </Col>
      <Col xs={12} sm={8} md={4}>
        <Card className={styles.statCard} loading={statsLoading}>
          <Space align='center' size="large">
            <div className={styles.statLabel}>
              {t('admin.logs.stats.errorRate', 'Error Rate')}
            </div>
            <Progress
              type="circle"
              percent={getErrorPercentage()}
              width={60}
              strokeColor={getErrorPercentage() > 50 ? '#ff4d4f' : getErrorPercentage() > 20 ? '#faad14' : '#52c41a'}
              format={(percent) => `${percent}%`}
            />
          </Space>
        </Card>
      </Col>
    </Row>
  );

  /**
   * Timeline chart configuration
   */
  const timelineConfig = {
    data: timelineData,
    xField: 'time',
    yField: 'value',
    seriesField: 'type',
    smooth: true,
    animation: {
      appear: {
        animation: 'path-in',
        duration: 800,
      },
    },
    colorField: 'type',
    scale: {
      color: {
        domain: ['Error', 'Warning', 'Critical', 'Information'],
        range: ['#ff4d4f', '#faad14', '#722ed1', '#1890ff'],
      },
    },
    legend: {
      position: 'top',
    },
    xAxis: {
      label: {
        autoRotate: true,
        autoHide: true,
      },
    },
    yAxis: {
      title: {
        text: t('admin.logs.timeline.count', 'Count'),
      },
    },
    tooltip: {
      shared: true,
      showMarkers: true,
    },
    point: {
      size: 3,
      shape: 'circle',
    },
  };

  /**
   * Render timeline chart section
   */
  const renderTimeline = () => (
    <Card 
      className={styles.timelineCard}
      title={
        <Space>
          <LineChartOutlined />
          {t('admin.logs.timeline.title', 'Exception Timeline')}
        </Space>
      }
      extra={
        <Segmented
          value={timelinePeriod}
          onChange={handleTimelinePeriodChange}
          options={[
            { label: t('admin.logs.timeline.day', 'Day'), value: TimelinePeriods.Day },
            { label: t('admin.logs.timeline.week', 'Week'), value: TimelinePeriods.Week },
            { label: t('admin.logs.timeline.month', 'Month'), value: TimelinePeriods.Month },
            { label: t('admin.logs.timeline.threeMonths', '3 Months'), value: TimelinePeriods.ThreeMonths },
          ]}
        />
      }
    >
      <Spin spinning={timelineLoading}>
        {timelineData.length > 0 ? (
          <div className={styles.chartContainer}>
            <Line {...timelineConfig} />
          </div>
        ) : (
          <Empty 
            description={t('admin.logs.timeline.noData', 'No data for selected period')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Spin>
    </Card>
  );

  /**
   * Render filter panel
   */
  const renderFilterPanel = () => (
    <Card className={styles.filterPanel} size="small">
      <Row gutter={[16, 16]} align="bottom">
        <Col xs={24} sm={12} md={6}>
          <div className={styles.filterLabel}>
            {t('admin.logs.filters.level', 'Log Level')}
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder={t('admin.logs.filters.allLevels', 'All Levels')}
            allowClear
            value={filters.level}
            onChange={(value) => setFilters({ ...filters, level: value })}
          >
            {Object.keys(LogLevels).map(level => (
              <Select.Option key={level} value={level}>
                <Space>
                  {getLevelIcon(level)}
                  {level}
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div className={styles.filterLabel}>
            {t('admin.logs.filters.dateRange', 'Date Range')}
          </div>
          <RangePicker
            style={{ width: '100%' }}
            showTime
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className={styles.filterLabel}>
            {t('admin.logs.filters.correlationId', 'Correlation ID')}
          </div>
          <Input
            placeholder={t('admin.logs.filters.correlationIdPlaceholder', 'Enter correlation ID')}
            value={filters.correlationId}
            onChange={(e) => setFilters({ ...filters, correlationId: e.target.value })}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Space>
            <Button type="primary" onClick={applyFilters} icon={<SearchOutlined />}>
              {t('common.apply', 'Apply')}
            </Button>
            <Button onClick={resetFilters} icon={<ClearOutlined />}>
              {t('common.reset', 'Reset')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      <Divider style={{ margin: '12px 0' }} />
      
      <div className={styles.timeRangeButtons}>
        <Text type="secondary" style={{ marginRight: 8 }}>
          {t('admin.logs.filters.quickRange', 'Quick Range:')}
        </Text>
        <Button
          size="small"
          onClick={() => {
            setFilters({
              ...filters,
              dateRange: [dayjs().subtract(1, 'hour'), dayjs()],
            });
          }}
        >
          Last Hour
        </Button>
        <Button
          size="small"
          onClick={() => {
            setFilters({
              ...filters,
              dateRange: [dayjs().subtract(24, 'hours'), dayjs()],
            });
          }}
        >
          Last 24 Hours
        </Button>
        <Button
          size="small"
          onClick={() => {
            setFilters({
              ...filters,
              dateRange: [dayjs().subtract(7, 'days'), dayjs()],
            });
          }}
        >
          Last 7 Days
        </Button>
        <Button
          size="small"
          onClick={() => {
            setFilters({
              ...filters,
              dateRange: [dayjs().subtract(30, 'days'), dayjs()],
            });
          }}
        >
          Last 30 Days
        </Button>
      </div>
    </Card>
  );

  /**
   * Render bulk delete modal
   */
  const renderBulkDeleteModal = () => (
    <Modal
      title={t('admin.logs.bulkDelete.title', 'Bulk Delete Logs')}
      open={bulkDeleteModalVisible}
      onCancel={() => setBulkDeleteModalVisible(false)}
      footer={null}
      width={600}
    >
      <Spin spinning={bulkDeleteLoading}>
        <div className={styles.bulkDeleteOptions}>
          <Alert
            message={t('admin.logs.bulkDelete.warning', 'This action cannot be undone')}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Title level={5}>{t('admin.logs.bulkDelete.byAge', 'Delete by Age')}</Title>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              onClick={() => handleBulkDelete({ olderThan: dayjs().subtract(7, 'days').toISOString() })}
            >
              Older than 7 days
            </Button>
            <Button
              onClick={() => handleBulkDelete({ olderThan: dayjs().subtract(30, 'days').toISOString() })}
            >
              Older than 30 days
            </Button>
            <Button
              onClick={() => handleBulkDelete({ olderThan: dayjs().subtract(90, 'days').toISOString() })}
            >
              Older than 90 days
            </Button>
          </Space>
          
          <Divider />
          
          <Title level={5}>{t('admin.logs.bulkDelete.byLevel', 'Delete by Level')}</Title>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              onClick={() => handleBulkDelete({ level: 'Information' })}
            >
              All Information Logs
            </Button>
            <Button
              onClick={() => handleBulkDelete({
                level: 'Information',
                olderThan: dayjs().subtract(7, 'days').toISOString(),
              })}
            >
              Information (older than 7 days)
            </Button>
          </Space>
          
          <div className={styles.dangerZone}>
            <Title level={5} className={styles.dangerTitle}>
              <ExclamationCircleOutlined /> {t('admin.logs.bulkDelete.dangerZone', 'Danger Zone')}
            </Title>
            <Popconfirm
              title={t('admin.logs.bulkDelete.deleteAllConfirm', 'Are you absolutely sure you want to delete ALL logs?')}
              onConfirm={handleDeleteAll}
              okText={t('common.yes', 'Yes, delete all')}
              cancelText={t('common.no', 'No')}
              okButtonProps={{ danger: true }}
            >
              <Button danger type="primary" icon={<DeleteOutlined />}>
                {t('admin.logs.bulkDelete.deleteAll', 'Delete All Logs')}
              </Button>
            </Popconfirm>
          </div>
        </div>
      </Spin>
    </Modal>
  );

  /**
   * Render log detail modal
   */
  const renderDetailModal = () => (
    <Modal
      title={
        <Space>
          {selectedLog && getLevelIcon(selectedLog.level)}
          {t('admin.logs.detail.title', 'Log Entry Details')}
        </Space>
      }
      open={detailModalVisible}
      onCancel={() => {
        setDetailModalVisible(false);
        setSelectedLog(null);
      }}
      footer={[
        <Button key="close" onClick={() => setDetailModalVisible(false)}>
          {t('common.close', 'Close')}
        </Button>,
        <Popconfirm
          key="delete"
          title={t('admin.logs.deleteConfirm', 'Delete this log entry?')}
          onConfirm={() => {
            handleDelete(selectedLog.id);
            setDetailModalVisible(false);
          }}
        >
          <Button danger icon={<DeleteOutlined />}>
            {t('common.delete', 'Delete')}
          </Button>
        </Popconfirm>,
      ]}
      width={800}
    >
      {selectedLog && (
        <div className={styles.logDetail}>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label={t('admin.logs.detail.id', 'ID')}>
              {selectedLog.id}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.logs.detail.level', 'Level')}>
              <span className={`${styles.levelBadge} ${getLevelClass(selectedLog.level)}`}>
                {getLevelIcon(selectedLog.level)}
                <span style={{ marginLeft: 4 }}>{selectedLog.level}</span>
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.logs.detail.timestamp', 'Timestamp')} span={2}>
              {logsService.formatTimestamp(selectedLog.timestamp)}
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({logsService.getRelativeTime(selectedLog.timestamp)})
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.logs.detail.correlationId', 'Correlation ID')} span={2}>
              {selectedLog.correlationId || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.logs.detail.ipAddress', 'IP Address')} span={2}>
              {getIpFromMeta(selectedLog.meta) ? (
                <Tag icon={<GlobalOutlined />} style={{ fontFamily: 'monospace' }}>
                  {getIpFromMeta(selectedLog.meta)}
                </Tag>
              ) : '-'}
            </Descriptions.Item>
          </Descriptions>
          
          <div className={styles.logDetailSection} style={{ marginTop: 16 }}>
            <Title level={5}>{t('admin.logs.detail.message', 'Message')}</Title>
            <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
              {selectedLog.message}
            </Paragraph>
          </div>
          
          {selectedLog.exception && (
            <div className={styles.logDetailSection}>
              <Title level={5}>
                <BugOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                {t('admin.logs.detail.exception', 'Exception Details')}
              </Title>
              <pre className={styles.exceptionBox}>
                {selectedLog.exception}
              </pre>
            </div>
          )}
          
          {selectedLog.meta && (
            <div className={styles.logDetailSection}>
              <Title level={5}>{t('admin.logs.detail.metadata', 'Metadata')}</Title>
              <pre className={styles.metadataBox}>
                {formatMetadata(selectedLog.meta)}
              </pre>
            </div>
          )}
          
          <Divider />
          
          <Descriptions column={2} size="small">
            <Descriptions.Item label={t('admin.logs.detail.dateCreated', 'Date Created')}>
              {logsService.formatTimestamp(selectedLog.dateCreated)}
            </Descriptions.Item>
            <Descriptions.Item label={t('admin.logs.detail.dateModified', 'Date Modified')}>
              {logsService.formatTimestamp(selectedLog.dateModified)}
            </Descriptions.Item>
          </Descriptions>
        </div>
      )}
    </Modal>
  );

  return (
    <PagesWrapper>
      <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleSection}>
              <ExclamationCircleOutlined className={styles.titleIcon} />
              <Title level={2} className={styles.title}>
                {t('admin.logs.title', 'Exception Logs')}
              </Title>
            </div>
            <Text type="secondary">
              {t('admin.logs.description', 'Monitor and manage system logs, exceptions, and errors')}
            </Text>
          </div>

          {/* Statistics Overview */}
          {renderStatistics()}

          {/* Timeline Chart */}
          {renderTimeline()}

          {/* Toolbar */}
          <div className={styles.toolbar}>
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
                type={showFilters ? 'primary' : 'default'}
              >
                {t('common.filters', 'Filters')}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchLogs(pagination.current, pagination.pageSize);
                  fetchStatistics();
                  fetchTimeline();
                }}
              >
                {t('common.refresh', 'Refresh')}
              </Button>
            </Space>
            <Space>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => setBulkDeleteModalVisible(true)}
              >
                {t('admin.logs.bulkDelete.button', 'Bulk Delete')}
              </Button>
            </Space>
          </div>

          {/* Filters Panel */}
          {showFilters && renderFilterPanel()}

          {/* Logs Table */}
          <Table
            columns={columns}
            dataSource={logs}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100', '200'],
              showTotal: (total, range) =>
                t('common.pagination', '{{start}}-{{end}} of {{total}} items', {
                  start: range[0],
                  end: range[1],
                  total,
                }),
            }}
            onChange={handleTableChange}
            scroll={{ x: 900 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('admin.logs.noLogs', 'No log entries found')}
                />
              ),
            }}
          />

        {/* Modals */}
        {renderDetailModal()}
        {renderBulkDeleteModal()}
      </div>
    </PagesWrapper>
  );
};

export default ExceptionLogs;
